import { createSlice } from "@reduxjs/toolkit";
import { Moment } from "moment";
import { TYPE_LEAVE } from "./getReasonLeave";
import { set } from "lodash";

export enum TypeSelect {
  SHORT_TERM = "SHORT_TERM",
  LONG_TERM = "LONG_TERM",
}

const leaveReasonMap = {
  BIRTHDAY: "totalSN",
  "SOCIAL-INSURANCE": "totalKLBH",
  "FULL-SALARY": "totalNL",
  "UNPAID-SALARY": "totalKLKBH",
} as const;

export const calculateTotalLeaveDay = (details: FormInterface["details"]) => {
  const LeaveDayInformation = {
    totalLeave: 0,
    totalNL: 0,
    totalKLBH: 0,
    totalKLKBH: 0,
    totalSN: 0,
  };
  if (!details || !details.length)
    return {
      totalLeave: "---",
      totalNL: "---",
      totalKLBH: "---",
      totalKLKBH: "---",
      totalSN: "---",
    };

  details.forEach((detail) => {
    const amount = detail.type === TYPE_LEAVE.FULL_DAY ? 1 : 0.5;
    const leaveMap = detail.leaveReasonTypeMap;
    if (!leaveMap || !Object.keys(leaveMap).length) return;

    for (const key in leaveReasonMap) {
      const leaveKey = key as keyof typeof leaveReasonMap;
      if (leaveMap[leaveKey]) {
        LeaveDayInformation[leaveReasonMap[leaveKey]] += leaveMap[leaveKey];
      }
    }

    LeaveDayInformation.totalLeave += amount;
  });
  return LeaveDayInformation;
};

interface ILeaveType {
  [key: string]: number;
}

interface FormInterface {
  ceoCode: string;
  codCode: string;
  details: {
    fromDate: Moment;
    reasonCode: string;
    type?: TYPE_LEAVE;
    reasonName?: string;
    toDate: Moment;
    leaveReasonTypeName?: string;
    leaveReasonType?:
      | "SOCIAL-INSURANCE"
      | "UNPAID-SALARY"
      | "FULL-SALARY"
      | "BIRTHDAY";
    id?: string;
    requireAttachFiles?: boolean;
    isNew?: boolean;
    hexColor?: string;
    leaveReasonTypeMap?: ILeaveType;
  }[];
  directorCode: string;
  files: {
    originalFilename: string;
    path: string;
  }[];
  files_files: any[];
  id?: string;
  leaderCode: string;
  note: string;
  relatedPersonCodes: string[];
  totalLeaveDay: 0;
  type: TypeSelect | null;
  approvals: {
    account: "string";
    departmentName: "string";
    employeeCode: "string";
    name: "string";
    positionName: "string";
  }[];
  numberChildren?: string;
}

interface InitialState {
  data: FormInterface;
  longTerm: {
    reasonName?: string;
    leaveReasonTypeName?: string;
    fromDate: Moment | null;
    toDate: Moment | null;
    type: TYPE_LEAVE;
    reasonCode: string;
    hexColor?: string;
    countHolidays?: boolean;
    countWeekends?: boolean;
    leaveReasonTypeMap?: ILeaveType;
    specificType?: string;
    maxDayForFeMale?: number;
    maxDayForMale?: number;
    minDayForFeMale?: number;
    minDayForMale?: number;
    rangeDateUnitForFeMale?: string;
    rangeDateUnitForMale?: string;
  };
  totalLeaveDay?: {
    totalLeave: number | string;
    totalNL: number | string;
    totalKLBH: number | string;
    totalKLKBH: number | string;
    totalSN: number | string;
  };
}

const initialState: InitialState = {
  data: {
    ceoCode: "",
    codCode: "",
    details: [],
    directorCode: "",
    files: [],
    files_files: [],
    leaderCode: "",
    note: "",
    relatedPersonCodes: [],
    totalLeaveDay: 0,
    type: TypeSelect.SHORT_TERM,
    approvals: [],
  },
  longTerm: {
    fromDate: null,
    toDate: null,
    type: null,
    reasonCode: "",
    reasonName: "",
  },
  totalLeaveDay: {
    totalLeave: 0,
    totalNL: 0,
    totalKLBH: 0,
    totalKLKBH: 0,
    totalSN: 0,
  },
};

const formReqLeaveSlice = createSlice({
  name: "formReqLeave",
  initialState,
  reducers: {
    updateFormReq: (state, action) => {
      const keys = Object.keys(action.payload);
      for (let i = 0; i < keys.length; i++) {
        set(state.data, keys[i], action.payload[keys[i]]);
      }
    },
    formReqLeaveUpdate: (state, action) => {
      Object.assign(state.data, action.payload);
    },
    formReqUpdateDetail: (state, action) => {
      state.data = {
        ...state.data,
        details: [...action.payload],
      };
    },
    formReqLeaveLongTermUpdate: (state, action) => {
      state.longTerm = { ...state.longTerm, ...action.payload };
    },
    resetFormLeaveLongTerm: (state) => {
      state.longTerm = initialState.longTerm;
    },

    formReqLeaveReset: () => {
      return { ...initialState };
    },
    updateTotalLeaveDay: (state, action) => {
      state.totalLeaveDay = action.payload;
    },
  },
});

export const {
  formReqLeaveUpdate,
  updateFormReq,
  formReqLeaveReset,
  formReqLeaveLongTermUpdate,
  resetFormLeaveLongTerm,
  formReqUpdateDetail,
  updateTotalLeaveDay,
} = formReqLeaveSlice.actions;

export default formReqLeaveSlice.reducer;
